import { useState } from 'react'
import type { HourStat, ShareStat } from '../../utils/reportStats'
import './ReportCharts.css'

/**
 * Rapor sayfasının grafikleri. Kütüphanesiz, düz HTML: çubuklar birer
 * düğme, yani dokunmayla ve klavyeyle de ayrıntı açılıyor. Değerler ayrıca
 * yazılı (etiket, açıklama ya da aşağıdaki tablolar); ipucu yalnızca kolaylık.
 */

/** Kuruşsuz, binlik ayraçlı: "1.250 ₺". Grafikte kuruş gürültü. */
export const tl = (kurus: number) => `${Math.round(kurus / 100).toLocaleString('tr-TR')} ₺`

/** Ekseni yuvarlak sayılara oturtan üst sınır: 1, 2, 2.5, 5 × 10ⁿ. */
function niceMax(v: number): number {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p
  return 10 * p
}

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0)

/* ---------- saatlik satış ---------- */

export function HourChart({ data }: { data: HourStat[] }) {
  const [on, setOn] = useState<number | null>(null)
  const max = niceMax(Math.max(...data.map((d) => d.kurus)))
  const peak = data.reduce((a, b) => (b.kurus > a.kurus ? b : a), data[0])
  const every = data.length > 12 ? 2 : 1
  const cur = on === null ? null : data.find((d) => d.hour === on)

  return (
    <div className="viz viz--hours">
      <div className="viz__plot">
        {/* Izgara: üç yatay çizgi, değerleri solda. */}
        <div className="viz__grid" aria-hidden="true">
          {[1, 0.5, 0].map((f) => (
            <div key={f} className="viz__gridline" style={{ bottom: `${f * 100}%` }}>
              <span>{tl(max * f)}</span>
            </div>
          ))}
        </div>
        <div className="viz__cols">
          {data.map((d) => {
            const h = pct(d.kurus, max)
            return (
              <button
                key={d.hour}
                type="button"
                className={`viz__col ${on === d.hour ? 'is-on' : ''}`}
                onPointerEnter={() => setOn(d.hour)}
                onPointerLeave={() => setOn(null)}
                onFocus={() => setOn(d.hour)}
                onBlur={() => setOn(null)}
                onClick={() => setOn(d.hour)}
                aria-label={`${d.hour}.00-${d.hour + 1}.00: ${tl(d.kurus)}, ${d.orders} sipariş`}
              >
                {d.kurus > 0 && <i style={{ height: `${Math.max(h, 1.5)}%` }} />}
                {/* Yalnız en yüksek saat yazılı; ötekiler ipucunda. */}
                {d === peak && d.kurus > 0 && on === null && (
                  <em style={{ bottom: `${h}%` }}>{tl(d.kurus)}</em>
                )}
              </button>
            )
          })}
        </div>
        {cur && (
          <div
            className="viz__tip"
            style={{ left: `${((data.indexOf(cur) + 0.5) / data.length) * 100}%` }}
            role="status"
          >
            <b>{tl(cur.kurus)}</b>
            <span>
              {String(cur.hour).padStart(2, '0')}.00-{String(cur.hour + 1).padStart(2, '0')}.00,{' '}
              {cur.orders} sipariş
            </span>
          </div>
        )}
      </div>
      <div className="viz__xaxis" aria-hidden="true">
        {data.map((d, i) => (
          <span key={d.hour}>{i % every === 0 ? String(d.hour).padStart(2, '0') : ''}</span>
        ))}
      </div>
    </div>
  )
}

/* ---------- sıralı çubuklar (kişi, ürün) ---------- */

export interface BarRow {
  key: string
  label: string
  /** Çubuğun boyu bu değere göre. */
  value: number
  /** Çubuğun ucunda yazan değer. */
  valueText: string
  /** Adın altında küçük, sönük satır. */
  sub?: string
}

/**
 * Yan yana iki liste aynı turuncuyla göz yoruyordu (kullanıcı, 11 Eki 2026):
 * her liste kendi sakin renginde, yalnız birinci sıra tam renk.
 */
export type BarTone = 'blue' | 'green' | 'brand'

export function BarList({ rows, tone = 'brand' }: { rows: BarRow[]; tone?: BarTone }) {
  const max = Math.max(...rows.map((r) => r.value), 1)
  return (
    <ul className={`viz viz--bars viz--${tone}`}>
      {rows.map((r, i) => (
        <li key={r.key}>
          <div className="viz__bar-head">
            <span className="viz__bar-name">
              <small>{i + 1}</small>
              {r.label}
            </span>
            <b>{r.valueText}</b>
          </div>
          <div className="viz__track">
            <i style={{ width: `${Math.max(pct(r.value, max), 1.5)}%` }} />
          </div>
          {r.sub && <span className="viz__bar-sub">{r.sub}</span>}
        </li>
      ))}
    </ul>
  )
}

/* ---------- kategori payı ---------- */

export function ShareBar({ data }: { data: ShareStat[] }) {
  const [on, setOn] = useState<string | null>(null)
  const total = data.reduce((s, d) => s + d.kurus, 0)
  return (
    <div className="viz viz--share">
      <div className="viz__stack" onPointerLeave={() => setOn(null)}>
        {data.map((d, i) => (
          <button
            key={d.name}
            type="button"
            className={`viz__seg viz__seg--${i + 1} ${on && on !== d.name ? 'is-dim' : ''}`}
            style={{ flexGrow: d.kurus }}
            onPointerEnter={() => setOn(d.name)}
            onFocus={() => setOn(d.name)}
            onBlur={() => setOn(null)}
            onClick={() => setOn(d.name)}
            aria-label={`${d.name}: ${tl(d.kurus)}, %${pct(d.kurus, total).toFixed(0)}`}
          />
        ))}
      </div>
      {/* Renk tek başına bir şey söylemesin: her rengin yanında adı, tutarı
          ve yüzdesi. */}
      <ul className="viz__legend">
        {data.map((d, i) => (
          <li
            key={d.name}
            className={on === d.name ? 'is-on' : on ? 'is-dim' : ''}
            onPointerEnter={() => setOn(d.name)}
            onPointerLeave={() => setOn(null)}
          >
            <i className={`viz__swatch viz__seg--${i + 1}`} />
            <span className="viz__legend-name">{d.name}</span>
            <b>{tl(d.kurus)}</b>
            <small>%{pct(d.kurus, total).toFixed(0)}</small>
          </li>
        ))}
      </ul>
    </div>
  )
}
